import React, { createContext, useState, useContext } from 'react';
import config from '../config';

interface AuthContextType {
  token: string | null;
  setAuthToken: (token: string) => void;
  login: (secretId?: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  verifyCSRF: (token: string | null) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem('auth_token');
  });

  const setAuthToken = (newToken: string) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
  };

  const setCSRF = () => {
    const csrfToken = Math.random().toString(36).substring(2);
    sessionStorage.setItem('csrfToken', csrfToken);
    return csrfToken;
  };

  const verifyCSRF = (token: string | null) => {
    const csrfToken = sessionStorage.getItem('csrfToken') || '';
    return !!token && token === csrfToken;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
  };

  const login = (secretId = '') => {
    // Set relay state to redirect user once logged in
    sessionStorage.setItem('secretId', secretId);

    // Generate a random token to prevent CSRF attacks
    const csrfToken = setCSRF()

    const params = new URLSearchParams({
      client_id: config.userPoolWebClientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: window.location.origin + '/callback',
      identity_provider: config.identityProviderName,
      state: csrfToken,
    });

    window.location.href = `${config.cognitoUrl}/oauth2/authorize?${params.toString()}`;
  };

  const isExpired = (jwt: string) => {
    try {
      const base64Url = jwt.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      return payload.exp * 1000 < Date.now();
    } catch(err) {
      console.log(err);
      return true;
    }
  };

  const isAuthenticated = !!token && !isExpired(token);

  return (
    <AuthContext.Provider value={{ token, setAuthToken, login, logout, isAuthenticated, verifyCSRF }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
